import * as THREE from 'three';
import type {
    ImportedModelBodyTreeSelectionFaceHighlighterDeps,
    ImportedModelTreeSelectionTarget,
} from '../../../types';
import type { EntityHighlightTarget } from '../types/entity-highlight-target';

/**
 * 将树业务范围收口为正式渲染 Mesh；不读取拓扑仓库或创建拾取宿主。
 * 已脱离的实例由 SDK 注册索引补齐，显式目标只能缩小选中根的范围，不能越界。
 */
export class ImportedModelTreeSelectionTargetResolver {
    /** 仅活动选择期间保存的业务范围，不因 hover 刷新而重扫场景。 */
    private readonly candidatesByRoot = new Map<THREE.Object3D, Set<THREE.Mesh>>();
    /** 非拥有型结构订阅，结束选择时对称退订。 */
    private unsubscribe: (() => void) | null = null;
    /** 缓存所对应的正式身份/实例版本。 */
    private revision = -1;
    /** @param deps 业务身份和正式实例来源查询端口。 */
    public constructor(private readonly deps: ImportedModelBodyTreeSelectionFaceHighlighterDeps) {}

    /**
     * 解析单选、多选与父子重复选择，完整范围优先于子集。
     * @param roots 当前选中业务对象。
     * @param explicitTargets 树节点声明的精确对象及 primitive 范围。
     * @returns 按 source 身份去重的渲染目标。
     */
    public resolve(
        roots: readonly THREE.Object3D[],
        explicitTargets?: readonly EntityHighlightTarget[],
    ): Map<THREE.Mesh, ImportedModelTreeSelectionTarget> {
        this.ensureRevision();
        // 选择 A→B 不继续持有 A；显式 group 范围允许在本轮按需重新进入缓存。
        const activeRoots = new Set(roots);
        for (const root of this.candidatesByRoot.keys())
            if (!activeRoots.has(root)) this.candidatesByRoot.delete(root);
        const result = new Map<THREE.Mesh, ImportedModelTreeSelectionTarget>();
        for (const root of roots) {
            if (!this.deps.isImportedModelObject(root)) continue;
            const candidates = new Set(
                [...this.collect(root)].filter((source) => this.isVisibleSource(source)),
            );
            if (!explicitTargets?.length) {
                for (const source of candidates) result.set(source, { source });
                continue;
            }
            for (const target of explicitTargets) {
                const object =
                    root.uuid === target.objectId
                        ? root
                        : (root.getObjectByProperty('uuid', target.objectId) ??
                          this.deps.findSceneObjectById(target.objectId));
                if (!object) continue;
                const directMesh = object instanceof THREE.Mesh && object.geometry.getAttribute('position')?.count > 0;
                const members = directMesh ? [object] : [...this.collect(object)];
                let primitiveOffset = 0;
                for (const source of members) {
                    const groupCount = source.geometry.groups.length;
                    const span = Math.max(groupCount, 1);
                    if (candidates.has(source)) {
                        // GLTF 单 primitive 子 Mesh 的 ID 已精确定位；原始 primitive 序号不等于 group 序号。
                        const indices =
                            target.primitiveIndices?.length &&
                            (groupCount > 0 || !directMesh)
                                ? target.primitiveIndices
                                      .filter(
                                          (index) =>
                                              Number.isInteger(index) &&
                                              index >= primitiveOffset &&
                                              index < primitiveOffset + span,
                                      )
                                      .map((index) => index - primitiveOffset)
                                : undefined;
                        if (!indices || indices.length > 0)
                            this.merge(result, source, groupCount > 0 ? indices : undefined);
                    }
                    primitiveOffset += span;
                }
            }
        }
        return result;
    }

    /**
     * 收集当前业务子树及其脱离 source，跳过完整派生子树及隐藏组件。
     * @param root 已解析的业务范围。
     * @returns 本范围内的正式来源，包含未加载拓扑资产的模型。
     */
    private collect(root: THREE.Object3D): Set<THREE.Mesh> {
        const cached = this.candidatesByRoot.get(root);
        if (cached) return cached;
        const meshes = new Set<THREE.Mesh>();
        // 不进入实例批次、拓扑宿主或选择组，不把派生对象当作下一轮模板。
        const visit = (object: THREE.Object3D): void => {
            if (this.isDerivedObject(object)) return;
            if (
                object instanceof THREE.Mesh &&
                !(object instanceof THREE.InstancedMesh) &&
                !object.userData.skipSelection
            )
                meshes.add(object);
            for (const child of this.deps.listSourceHierarchyChildren?.(object) ?? object.children) visit(child);
        };
        visit(root);
        // 新 SDK 直接沿有序逻辑树访问脱离来源，旧注入端口仍可通过范围索引补齐。
        for (const source of this.deps.listSourceHierarchyChildren ? [] : this.deps.listInstancedSourceMeshes(root)) {
            const unit = this.deps.resolveInstancedMeshUnit(source);
            if (!unit) continue;
            visit(source);
        }
        if (this.unsubscribe && this.deps.getSourceRevision) this.candidatesByRoot.set(root, meshes);
        return meshes;
    }

    /** 清空缓存并解除订阅，不能在 Viewer 销毁后保留业务对象。 */
    public reset(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.candidatesByRoot.clear();
        this.revision = -1;
    }

    /** 根据 SDK 成员事件和语义版本使范围失效；派生选择/拓扑事件不使自己反复失效。 */
    private ensureRevision(): void {
        if (!this.unsubscribe && this.deps.membershipSource) {
            const invalidate = (object: THREE.Object3D): void => {
                if (this.isDerivedObject(object)) return;
                this.candidatesByRoot.clear();
            };
            this.unsubscribe = this.deps.membershipSource.subscribe({
                onObjectAdded: invalidate,
                onObjectRemoved: invalidate,
                onObjectOrderChanged: () => this.candidatesByRoot.clear(),
            });
        }
        const revision = this.deps.getSourceRevision?.() ?? -1;
        if (revision !== this.revision) {
            this.candidatesByRoot.clear();
            this.revision = revision;
        }
    }

    /** @param object 遍历或成员事件对象。 @returns 是否应从业务范围及其失效事件中排除。 */
    private isDerivedObject(object: THREE.Object3D): boolean {
        return Boolean(object.userData.isImportedModelOverlay || object.userData.skipSelectionSubtree
            || object.name.startsWith('imported-topology-host:') || Reflect.get(object, 'isFeatureFace')
            || Reflect.get(object, 'isFeatureLine') || Reflect.get(object, 'isFeaturePoint'));
    }

    /** @param source 已解析的正式来源。 @returns 当前业务可见且存在渲染几何时返回 true。 */
    private isVisibleSource(source: THREE.Mesh): boolean {
        const unit = this.deps.resolveInstancedMeshUnit(source);
        if ((!source.visible && !unit) || !(source.geometry.getAttribute('position')?.count > 0))
            return false;
        let parent =
            source.parent ??
            unit?.resolveSourceMeshOriginalParent(source) ??
            unit?.resolveOccurrenceRoot(source);
        // 脱离 source 自身不充当隐藏的逻辑父级，但其权威祖先仍控制业务可见性。
        while (parent && parent !== source) {
            if (!parent.visible) return false;
            parent =
                parent.parent ??
                (parent instanceof THREE.Mesh
                    ? this.deps.resolveInstancedMeshUnit(parent)?.resolveSourceMeshOriginalParent(parent)
                    : null);
        }
        return true;
    }

    /**
     * 合并相同 source 的显式 primitive 范围。
     * @param targets 本轮结果。
     * @param source 正式渲染来源。
     * @param indices 当前局部子集。
     */
    private merge(
        targets: Map<THREE.Mesh, ImportedModelTreeSelectionTarget>,
        source: THREE.Mesh,
        indices?: readonly number[],
    ): void {
        const previous = targets.get(source);
        if (previous && !previous.primitiveIndices) return;
        const merged = indices
            ? [...new Set([...(previous?.primitiveIndices ?? []), ...indices])].sort(
                  (left, right) => left - right,
              )
            : undefined;
        targets.set(source, {
            source,
            primitiveIndices: merged?.length === source.geometry.groups.length ? undefined : merged,
        });
    }
}
